/**
 * AssessmentWizard — 7-stegs kartläggningsformulär.
 *
 * Props:
 *   assessmentId  – null = ny, string = redigera
 *   initialData   – förifyller steg 1 vid ny (t.ex. participant_id)
 *   onClose       – stäng utan fastställande
 *   onSaved       – callback med assessment-objekt efter sparande/fastställande
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import {
  CheckCircle2, Circle, ChevronLeft, ChevronRight, X,
  Plus, Trash2, ShieldCheck, AlertCircle,
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

// ── Katalogdata ────────────────────────────────────────────────────────────────

const ASSESSMENT_TYPE_LABELS = {
  Inledande_kartlaggning: 'Inledande kartläggning',
  Uppfoljande_kartlaggning: 'Uppföljande kartläggning',
  Revidering: 'Revidering',
  Infor_ny_insats: 'Inför ny insats',
  Infor_avslut: 'Inför avslut',
};

const EVIDENCE_SOURCES = [
  'Samtal med deltagare',
  'Observation',
  'Arbetsplatsobservation',
  'Tidigare kartläggning',
  'Extern remiss',
  'Dokumenterat underlag',
  'Annan källa',
];

const RELIABILITY_OPTIONS = [
  { value: 'Preliminar', label: 'Preliminär' },
  { value: 'Tillracklig', label: 'Tillräcklig' },
  { value: 'Verifierad_i_flera_kallor', label: 'Verifierad i flera källor' },
];

export const AREA_NAMES = [
  'Självständighet',
  'Social förmåga i arbetsmiljö',
  'Motivation',
  'Uthållighet',
  'Ansvarstagande',
  'Arbetstempo',
  'Förmåga att ta instruktioner',
  'Struktur och planering',
  'Närvaro och punktlighet',
  'Belastningstolerans',
  'Kommunikationsförmåga',
  'Behov av stöd i arbetsmoment',
];

const LEVEL_OPTIONS = [
  { value: 'Ej_bedomd', label: 'Ej bedömd' },
  { value: 'Begransad', label: 'Begränsad' },
  { value: 'Delvis_fungerande', label: 'Delvis fungerande' },
  { value: 'Fungerande_med_stod', label: 'Fungerande med stöd' },
  { value: 'Fungerande_sjalvstandigt', label: 'Fungerande självständigt' },
];

const LEVEL_COLORS = {
  Ej_bedomd: 'bg-gray-100 text-gray-500',
  Begransad: 'bg-red-100 text-red-700',
  Delvis_fungerande: 'bg-orange-100 text-orange-700',
  Fungerande_med_stod: 'bg-yellow-100 text-yellow-700',
  Fungerande_sjalvstandigt: 'bg-green-100 text-green-700',
};

const PRIORITY_OPTIONS = [
  { value: '', label: '— Ingen —' },
  { value: 'Lag', label: 'Låg' },
  { value: 'Medel', label: 'Medel' },
  { value: 'Hog', label: 'Hög' },
  { value: 'Kritisk', label: 'Kritisk' },
];

const ADAPTATION_CATALOG = [
  { name: 'Ergonomisk arbetsplats', main_group: 'Fysiska' },
  { name: 'Hjälpmedel', main_group: 'Fysiska' },
  { name: 'Anpassat arbetstempo', main_group: 'Fysiska' },
  { name: 'Reducerad arbetstid', main_group: 'Fysiska' },
  { name: 'Flexibla arbetstider', main_group: 'Fysiska' },
  { name: 'Stödjande arbetsklimat', main_group: 'Psykosociala' },
  { name: 'Tydliga förväntningar', main_group: 'Psykosociala' },
  { name: 'Regelbundna avstämningar', main_group: 'Psykosociala' },
  { name: 'Mentorskap', main_group: 'Psykosociala' },
  { name: 'Reducerat stressmoment', main_group: 'Psykosociala' },
  { name: 'Skriftliga instruktioner', main_group: 'Pedagogiska' },
  { name: 'Steg-för-steg-handledning', main_group: 'Pedagogiska' },
  { name: 'Visuella stödstrukturer', main_group: 'Pedagogiska' },
  { name: 'Extra inlärningstid', main_group: 'Pedagogiska' },
  { name: 'Konkret feedback', main_group: 'Pedagogiska' },
  { name: 'Tydlig arbetsstruktur', main_group: 'Organisatoriska' },
  { name: 'Anpassade arbetsuppgifter', main_group: 'Organisatoriska' },
  { name: 'Rotation av uppgifter', main_group: 'Organisatoriska' },
  { name: 'Delat ansvar', main_group: 'Organisatoriska' },
  { name: 'Tolk', main_group: 'Kommunikationsstöd' },
  { name: 'Bildstöd', main_group: 'Kommunikationsstöd' },
  { name: 'Enkel kommunikation', main_group: 'Kommunikationsstöd' },
  { name: 'Digitalt stöd', main_group: 'Kommunikationsstöd' },
  { name: 'Ökad handledartid', main_group: 'Handledarstöd' },
  { name: 'Regelbunden coachning', main_group: 'Handledarstöd' },
  { name: 'Strukturerade möten', main_group: 'Handledarstöd' },
  { name: 'Arbetsplatshandledare', main_group: 'Handledarstöd' },
];

const ADAPTATION_STATUS_OPTIONS = [
  { value: 'Rekommenderad', label: 'Rekommenderad' },
  { value: 'Beslutad', label: 'Beslutad' },
  { value: 'Genomford', label: 'Genomförd' },
];

const READINESS_OPTIONS = [
  { value: 'Redo_nu', label: 'Redo nu' },
  { value: 'Redo_med_stod', label: 'Redo med stöd' },
  { value: 'Ej_redo_annu', label: 'Ej redo ännu' },
];

const FOLLOWUP_FREQ_OPTIONS = [
  { value: 'Veckovis', label: 'Veckovis' },
  { value: 'Varannan_vecka', label: 'Varannan vecka' },
  { value: 'Manadsvis', label: 'Månadsvis' },
  { value: 'Kvartalsvis', label: 'Kvartalsvis' },
];

const FOLLOWUP_FORM_OPTIONS = [
  { value: 'Mote', label: 'Möte' },
  { value: 'Telefon', label: 'Telefon' },
  { value: 'Arbetsplatsbesok', label: 'Arbetsplatsbesök' },
  { value: 'Digitalt', label: 'Digitalt' },
  { value: 'Annat', label: 'Annat' },
];

const STEP_LABELS = [
  'Grunduppgifter',
  'Underlag',
  'Bedömning',
  'Anpassningar',
  'Mål',
  'Uppföljning',
  'Sammanfattning',
];

// ── Helpers ────────────────────────────────────────────────────────────────────

const today = () => new Date().toISOString().split('T')[0];

function nextDateFromFrequency(freq) {
  const d = new Date();
  if (freq === 'Veckovis') d.setDate(d.getDate() + 7);
  else if (freq === 'Varannan_vecka') d.setDate(d.getDate() + 14);
  else if (freq === 'Manadsvis') d.setMonth(d.getMonth() + 1);
  else if (freq === 'Kvartalsvis') d.setMonth(d.getMonth() + 3);
  return d.toISOString().split('T')[0];
}

const emptyAdaptation = () => ({
  adaptation_name: '',
  main_group: '',
  linked_area_id: '',
  priority: 'Medel',
  responsible_user_id: '',
  status: 'Rekommenderad',
  comment: '',
});

const initialAreas = () =>
  AREA_NAMES.map((name) => ({
    area_name: name,
    current_level: 'Ej_bedomd',
    strengths: '',
    obstacles: '',
    support_needs: '',
    comment: '',
    priority: '',
  }));

const selectCls = (err) =>
  `w-full h-10 px-3 border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring ${
    err ? 'border-destructive focus:ring-destructive' : 'border-input'
  }`;

// ── Completeness checks ────────────────────────────────────────────────────────

function isStep1Complete(s1) {
  return !!(s1.participant_id && s1.assessment_type && s1.start_date && s1.responsible_user_id && s1.purpose.trim());
}
function isStep2Complete(s2) {
  return !!(s2.sources.length > 0 && s2.evidence_date && s2.conducted_by && s2.reliability);
}
function isStep3Complete(areas) {
  return areas.some((a) => a.current_level !== 'Ej_bedomd');
}
function isStep4Complete() { return true; }
function isStep5Complete(s5) {
  return !!(s5.main_goal.trim() && s5.motivation.trim() && s5.recommended_next_intervention && s5.readiness);
}
function isStep6Complete(s6) {
  return !!(s6.frequency && s6.next_date && s6.responsible_user_id && s6.followup_form && s6.focus.trim());
}

// ── Sub-components ─────────────────────────────────────────────────────────────

const StepIndicator = ({ current, completeness }) => (
  <div className="flex items-center justify-between mb-8 px-2" data-testid="step-indicator">
    {STEP_LABELS.map((label, i) => {
      const step = i + 1;
      const isActive = step === current;
      const isDone = completeness[step];
      return (
        <React.Fragment key={step}>
          <div className="flex flex-col items-center gap-1 min-w-0">
            <div
              data-testid={`step-dot-${step}`}
              className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                isActive
                  ? 'bg-primary text-primary-foreground ring-2 ring-primary ring-offset-2'
                  : isDone
                  ? 'bg-green-500 text-white'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              {isDone && !isActive ? <CheckCircle2 size={16} /> : step}
            </div>
            <span className={`text-xs text-center leading-tight max-w-[60px] ${isActive ? 'font-bold text-foreground' : 'text-muted-foreground'}`}>
              {label}
            </span>
          </div>
          {i < STEP_LABELS.length - 1 && (
            <div className={`flex-1 h-0.5 mx-1 ${isDone ? 'bg-green-400' : 'bg-muted'}`} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

// ══════════════════════════════════════════════════════════════════════════════
// WIZARD
// ══════════════════════════════════════════════════════════════════════════════

const AssessmentWizard = ({ assessmentId: initialId = null, initialData = {}, onClose, onSaved }) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [assessmentId, setAssessmentId] = useState(initialId);
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [establishing, setEstablishing] = useState(false);
  const [showGoalDialog, setShowGoalDialog] = useState(false);
  const [establishedAssessment, setEstablishedAssessment] = useState(null);
  const [errors, setErrors] = useState({});
  const [isLocked, setIsLocked] = useState(false);

  // Data sources
  const [participants, setParticipants] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [enrollments, setEnrollments] = useState([]);

  // Step states
  const [s1, setS1] = useState({
    participant_id: initialData.participant_id || '',
    assessment_type: 'Inledande_kartlaggning',
    start_date: today(),
    responsible_user_id: user?.id || '',
    purpose: '',
    background: '',
    entity_name: '',
    enrollment_id: '',
  });

  const [s2, setS2] = useState({
    sources: [],
    evidence_date: today(),
    conducted_by: user?.id || '',
    reliability: '',
    notes: '',
  });

  const [areas, setAreas] = useState(initialAreas());
  const [adaptations, setAdaptations] = useState([]);

  const [s5, setS5] = useState({
    main_goal: '',
    sub_goals: [],
    recommended_next_intervention: '',
    motivation: '',
    readiness: '',
    risk_factors: '',
    other_recommendations: '',
  });

  const [s6, setS6] = useState({
    frequency: 'Manadsvis',
    next_date: nextDateFromFrequency('Manadsvis'),
    responsible_user_id: user?.id || '',
    followup_form: 'Mote',
    focus: '',
    early_trigger: '',
    reminder_enabled: false,
  });

  // ── Load data ────────────────────────────────────────────────────────────────
  useEffect(() => {
    api.get('/participants').then((r) => setParticipants(r.data || [])).catch(() => {});
    api.get('/users').then((r) => setOrgUsers(r.data || [])).catch(() => {});
    api.get('/catalog/verksamheter').then((r) => setCatalog(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (s1.participant_id) {
      api.get(`/participants/${s1.participant_id}/enrollments`)
        .then((r) => setEnrollments(r.data || []))
        .catch(() => setEnrollments([]));
    } else {
      setEnrollments([]);
    }
  }, [s1.participant_id]);

  // Load existing if editing
  useEffect(() => {
    if (!initialId) return;
    api.get(`/org/assessments/${initialId}`).then((r) => {
      const d = r.data;
      setIsLocked(d.status === 'Faststalld');
      setS1({
        participant_id: d.participant_id || '',
        assessment_type: d.assessment_type || 'Inledande_kartlaggning',
        start_date: d.start_date || today(),
        responsible_user_id: d.responsible_user_id || '',
        purpose: d.purpose || '',
        background: d.background || '',
        entity_name: d.entity_name || '',
        enrollment_id: d.enrollment_id || '',
      });
      if (d.evidence) {
        setS2({
          sources: d.evidence.sources || [],
          evidence_date: d.evidence.evidence_date || today(),
          conducted_by: d.evidence.conducted_by || '',
          reliability: d.evidence.reliability || '',
          notes: d.evidence.notes || '',
        });
      }
      if (d.areas && d.areas.length > 0) {
        setAreas(
          AREA_NAMES.map((name) => {
            const found = d.areas.find((a) => a.area_name === name);
            return found
              ? { area_name: name, current_level: found.current_level || 'Ej_bedomd', strengths: found.strengths || '', obstacles: found.obstacles || '', support_needs: found.support_needs || '', comment: found.comment || '', priority: found.priority || '' }
              : { area_name: name, current_level: 'Ej_bedomd', strengths: '', obstacles: '', support_needs: '', comment: '', priority: '' };
          })
        );
      }
      if (d.adaptations) {
        setAdaptations(d.adaptations.map((a) => ({
          adaptation_name: a.adaptation_name || '',
          main_group: a.main_group || '',
          linked_area_id: a.linked_area_id || '',
          priority: a.priority || 'Medel',
          responsible_user_id: a.responsible_user_id || '',
          status: a.status || 'Rekommenderad',
          comment: a.comment || '',
        })));
      }
      if (d.goal) {
        setS5({
          main_goal: d.goal.main_goal || '',
          sub_goals: d.goal.sub_goals || [],
          recommended_next_intervention: d.goal.recommended_next_intervention || '',
          motivation: d.goal.motivation || '',
          readiness: d.goal.readiness || '',
          risk_factors: d.goal.risk_factors || '',
          other_recommendations: d.goal.other_recommendations || '',
        });
      }
      if (d.followup_plan) {
        setS6({
          frequency: d.followup_plan.frequency || 'Manadsvis',
          next_date: d.followup_plan.next_date || nextDateFromFrequency('Manadsvis'),
          responsible_user_id: d.followup_plan.responsible_user_id || '',
          followup_form: d.followup_plan.followup_form || 'Mote',
          focus: d.followup_plan.focus || '',
          early_trigger: d.followup_plan.early_trigger || '',
          reminder_enabled: d.followup_plan.reminder_enabled || false,
        });
      }
    }).catch(() => toast.error('Kunde inte ladda kartläggning'));
  }, [initialId]);

  // ── Completeness ──────────────────────────────────────────────────────────────
  const completeness = {
    1: isStep1Complete(s1),
    2: isStep2Complete(s2),
    3: isStep3Complete(areas),
    4: isStep4Complete(),
    5: isStep5Complete(s5),
    6: isStep6Complete(s6),
    7: false,
  };

  const canEstablish = [1, 2, 3, 5, 6].every((s) => completeness[s]);

  // ── Save helpers ──────────────────────────────────────────────────────────────
  const buildPatchPayload = useCallback(() => ({
    assessment_type: s1.assessment_type,
    start_date: s1.start_date,
    responsible_user_id: s1.responsible_user_id,
    purpose: s1.purpose,
    background: s1.background || null,
    entity_name: s1.entity_name || null,
    enrollment_id: s1.enrollment_id || null,
    evidence: s2.sources.length > 0 ? {
      sources: s2.sources,
      evidence_date: s2.evidence_date,
      conducted_by: s2.conducted_by,
      reliability: s2.reliability,
      notes: s2.notes || null,
    } : undefined,
    areas: areas.map(({ area_name, current_level, strengths, obstacles, support_needs, comment, priority }) => ({
      area_name, current_level,
      strengths: strengths || null,
      obstacles: obstacles || null,
      support_needs: support_needs || null,
      comment: comment || null,
      priority: priority || null,
    })),
    adaptations: adaptations.map((a) => ({
      adaptation_name: a.adaptation_name,
      main_group: a.main_group,
      linked_area_id: a.linked_area_id || null,
      priority: a.priority,
      responsible_user_id: a.responsible_user_id || null,
      status: a.status,
      comment: a.comment || null,
    })),
    goal: s5.main_goal ? {
      main_goal: s5.main_goal,
      sub_goals: s5.sub_goals,
      recommended_next_intervention: s5.recommended_next_intervention,
      motivation: s5.motivation,
      readiness: s5.readiness,
      risk_factors: s5.risk_factors || null,
      other_recommendations: s5.other_recommendations || null,
    } : undefined,
    followup_plan: s6.focus ? {
      frequency: s6.frequency,
      next_date: s6.next_date,
      responsible_user_id: s6.responsible_user_id,
      followup_form: s6.followup_form,
      focus: s6.focus,
      early_trigger: s6.early_trigger || null,
      reminder_enabled: s6.reminder_enabled,
    } : undefined,
  }), [s1, s2, areas, adaptations, s5, s6]);

  const autosave = useCallback(async (id) => {
    if (!id || isLocked) return;
    try {
      await api.patch(`/org/assessments/${id}`, buildPatchPayload());
    } catch {
      // autosave — silent fail
    }
  }, [buildPatchPayload, isLocked]);

  // ── Step 1 validate + create/autosave ─────────────────────────────────────────
  const validateStep1 = () => {
    const errs = {};
    if (!s1.participant_id) errs.participant_id = 'Deltagare krävs';
    if (!s1.assessment_type) errs.assessment_type = 'Kartläggningstyp krävs';
    if (!s1.start_date) errs.start_date = 'Startdatum krävs';
    if (!s1.responsible_user_id) errs.responsible_user_id = 'Ansvarig handläggare krävs';
    if (!s1.purpose.trim()) errs.purpose = 'Syfte krävs';
    return errs;
  };

  const validateStep2 = () => {
    const errs = {};
    if (s2.sources.length === 0) errs.sources = 'Minst en källa krävs';
    if (!s2.evidence_date) errs.evidence_date = 'Datum krävs';
    if (!s2.conducted_by) errs.conducted_by = 'Utfört av krävs';
    if (!s2.reliability) errs.reliability = 'Tillförlitlighet krävs';
    return errs;
  };

  const validateStep3 = () => {
    if (!areas.some((a) => a.current_level !== 'Ej_bedomd')) {
      return { areas: 'Minst ett område måste bedömas' };
    }
    return {};
  };

  const validateStep5 = () => {
    const errs = {};
    if (!s5.main_goal.trim()) errs.main_goal = 'Huvudmål krävs';
    if (!s5.motivation.trim()) errs.motivation = 'Motivering krävs';
    if (!s5.recommended_next_intervention) errs.recommended_next_intervention = 'Rekommenderad insats krävs';
    if (!s5.readiness) errs.readiness = 'Startberedskap krävs';
    return errs;
  };

  const validateStep6 = () => {
    const errs = {};
    if (!s6.frequency) errs.frequency = 'Frekvens krävs';
    if (!s6.next_date) errs.next_date = 'Datum krävs';
    if (!s6.responsible_user_id) errs.responsible_user_id = 'Ansvarig uppföljare krävs';
    if (!s6.followup_form) errs.followup_form = 'Uppföljningsform krävs';
    if (!s6.focus.trim()) errs.focus = 'Vad ska följas upp krävs';
    return errs;
  };

  const STEP_VALIDATORS = { 1: validateStep1, 2: validateStep2, 3: validateStep3, 5: validateStep5, 6: validateStep6 };

  const goToStep = async (target) => {
    // Validate current step before moving forward
    if (target > step && STEP_VALIDATORS[step]) {
      const errs = STEP_VALIDATORS[step]();
      if (Object.keys(errs).length > 0) {
        setErrors(errs);
        toast.error('Rätta felen innan du fortsätter');
        return;
      }
    }
    setErrors({});

    // Step 1: create or autosave
    if (step === 1 && !assessmentId) {
      setSaving(true);
      try {
        const res = await api.post('/org/assessments', {
          participant_id: s1.participant_id,
          assessment_type: s1.assessment_type,
          start_date: s1.start_date,
          responsible_user_id: s1.responsible_user_id,
          purpose: s1.purpose,
          background: s1.background || null,
          entity_name: s1.entity_name || null,
          enrollment_id: s1.enrollment_id || null,
        });
        setAssessmentId(res.data.id);
        setAreas(initialAreas());
        toast.success('Kartläggning skapad som utkast');
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Kunde inte skapa kartläggning');
        setSaving(false);
        return;
      } finally {
        setSaving(false);
      }
    } else if (assessmentId && !isLocked) {
      await autosave(assessmentId);
    }

    setStep(target);
    window.scrollTo(0, 0);
  };

  // ── Actions ───────────────────────────────────────────────────────────────────
  const handleSaveDraft = async () => {
    if (!assessmentId) return;
    setSaving(true);
    try {
      await api.patch(`/org/assessments/${assessmentId}`, buildPatchPayload());
      await api.patch(`/org/assessments/${assessmentId}/status`, { status: 'Utkast' });
      toast.success('Sparad som utkast');
      if (onSaved) onSaved({ id: assessmentId, status: 'Utkast' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte spara');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkOngoing = async () => {
    if (!assessmentId) return;
    setSaving(true);
    try {
      await api.patch(`/org/assessments/${assessmentId}`, buildPatchPayload());
      await api.patch(`/org/assessments/${assessmentId}/status`, { status: 'Pagaende_bedomning' });
      toast.success('Markerad som pågående');
      if (onSaved) onSaved({ id: assessmentId, status: 'Pagaende_bedomning' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte uppdatera status');
    } finally {
      setSaving(false);
    }
  };

  const handleEstablish = async () => {
    if (!assessmentId) return;
    setEstablishing(true);
    try {
      await api.patch(`/org/assessments/${assessmentId}`, buildPatchPayload());
      const res = await api.post(`/org/assessments/${assessmentId}/establish`);
      toast.success('Kartläggning fastställd!');
      if (onSaved) onSaved(res.data);
      setEstablishedAssessment(res.data);
      setShowGoalDialog(true);
    } catch (err) {
      const detail = err.response?.data;
      if (detail?.missing) {
        toast.error('Kartläggningen är ofullständig: ' + detail.missing.join(', '));
      } else {
        toast.error(detail?.detail || 'Kunde inte fastställa');
      }
    } finally {
      setEstablishing(false);
    }
  };

  const handleGoalDialogClose = () => {
    setShowGoalDialog(false);
    if (onClose) onClose();
  };

  const handleNavigateToGoals = async () => {
    setShowGoalDialog(false);
    try {
      const sugRes = await api.get(`/org/assessments/${establishedAssessment.id}/goal-suggestion`);
      navigate('/goals', {
        state: {
          goalSuggestion: {
            ...sugRes.data,
            assessment_id: establishedAssessment.id,
            participant_id: establishedAssessment.participant_id,
          },
        },
      });
    } catch {
      navigate('/goals');
    }
    if (onClose) onClose();
  };

  // ── Render helpers ─────────────────────────────────────────────────────────────

  const getNextInterventionOptions = () => {
    const from_catalog = catalog.flatMap((c) =>
      c.kategorier.flatMap((k) => k.insatser.map((i) => i.insats))
    );
    return ['Fortsatt kartläggning', 'Ingen insats ännu', ...from_catalog];
  };

  const userName = (id) => {
    const u = orgUsers.find((u) => u.id === id);
    return u ? u.name : id;
  };

  const participantName = (id) => {
    const p = participants.find((p) => p.id === id);
    if (!p) return id;
    return [p.first_name, p.last_name].filter(Boolean).join(' ') || p.name || id;
  };

  // ── Steps ──────────────────────────────────────────────────────────────────────

  const renderStep1 = () => (
    <div className="space-y-5" data-testid="wizard-step-1">
      <div className="space-y-1.5">
        <Label htmlFor="participant">Deltagare <span className="text-destructive">*</span></Label>
        <select
          id="participant"
          data-testid="s1-participant"
          value={s1.participant_id}
          onChange={(e) => { setS1((p) => ({ ...p, participant_id: e.target.value })); setErrors((p) => ({ ...p, participant_id: '' })); }}
          className={selectCls(errors.participant_id)}
          disabled={isLocked}
        >
          <option value="">Välj deltagare...</option>
          {participants.map((p) => (
            <option key={p.id} value={p.id}>{participantName(p.id)}</option>
          ))}
        </select>
        {errors.participant_id && <p role="alert" className="text-xs text-destructive">{errors.participant_id}</p>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="type">Kartläggningstyp <span className="text-destructive">*</span></Label>
          <select
            id="type"
            data-testid="s1-type"
            value={s1.assessment_type}
            onChange={(e) => { setS1((p) => ({ ...p, assessment_type: e.target.value })); setErrors((p) => ({ ...p, assessment_type: '' })); }}
            className={selectCls(errors.assessment_type)}
            disabled={isLocked}
          >
            {Object.entries(ASSESSMENT_TYPE_LABELS).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          {errors.assessment_type && <p role="alert" className="text-xs text-destructive">{errors.assessment_type}</p>}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="start-date">Startdatum <span className="text-destructive">*</span></Label>
          <Input
            id="start-date"
            type="date"
            data-testid="s1-start-date"
            value={s1.start_date}
            onChange={(e) => { setS1((p) => ({ ...p, start_date: e.target.value })); setErrors((p) => ({ ...p, start_date: '' })); }}
            className={errors.start_date ? 'border-destructive' : ''}
            disabled={isLocked}
          />
          {errors.start_date && <p role="alert" className="text-xs text-destructive">{errors.start_date}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="responsible">Ansvarig handläggare <span className="text-destructive">*</span></Label>
          <select
            id="responsible"
            data-testid="s1-responsible"
            value={s1.responsible_user_id}
            onChange={(e) => { setS1((p) => ({ ...p, responsible_user_id: e.target.value })); setErrors((p) => ({ ...p, responsible_user_id: '' })); }}
            className={selectCls(errors.responsible_user_id)}
            disabled={isLocked}
          >
            <option value="">Välj handläggare...</option>
            {orgUsers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          {errors.responsible_user_id && <p role="alert" className="text-xs text-destructive">{errors.responsible_user_id}</p>}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="entity">Enhet</Label>
          <Input
            id="entity"
            data-testid="s1-entity"
            value={s1.entity_name}
            onChange={(e) => setS1((p) => ({ ...p, entity_name: e.target.value }))}
            disabled={isLocked}
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="purpose">Syfte <span className="text-destructive">*</span></Label>
        <Textarea
          id="purpose"
          data-testid="s1-purpose"
          value={s1.purpose}
          onChange={(e) => { setS1((p) => ({ ...p, purpose: e.target.value })); setErrors((p) => ({ ...p, purpose: '' })); }}
          rows={3}
          className={errors.purpose ? 'border-destructive' : ''}
          disabled={isLocked}
        />
        {errors.purpose && <p role="alert" className="text-xs text-destructive">{errors.purpose}</p>}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="background">Kort bakgrund</Label>
        <Textarea id="background" data-testid="s1-background" value={s1.background} onChange={(e) => setS1((p) => ({ ...p, background: e.target.value }))} rows={2} disabled={isLocked} />
      </div>

      {enrollments.length > 0 && (
        <div className="space-y-1.5">
          <Label htmlFor="enrollment">Pågående/tidigare verksamhetskoppling</Label>
          <select
            id="enrollment"
            data-testid="s1-enrollment"
            value={s1.enrollment_id}
            onChange={(e) => setS1((p) => ({ ...p, enrollment_id: e.target.value }))}
            className={selectCls(false)}
            disabled={isLocked}
          >
            <option value="">Ingen vald</option>
            {enrollments.map((e) => (
              <option key={e.id} value={e.id}>{e.area} — {e.intervention} ({e.start_date})</option>
            ))}
          </select>
        </div>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-5" data-testid="wizard-step-2">
      <div className="space-y-2">
        <Label>Bedömningskälla <span className="text-destructive">*</span></Label>
        {errors.sources && <p role="alert" className="text-xs text-destructive">{errors.sources}</p>}
        <div className="flex flex-wrap gap-2" data-testid="s2-sources">
          {EVIDENCE_SOURCES.map((src) => (
            <button
              key={src}
              type="button"
              data-testid={`source-${src}`}
              onClick={() => {
                setS2((p) => ({
                  ...p,
                  sources: p.sources.includes(src)
                    ? p.sources.filter((x) => x !== src)
                    : [...p.sources, src],
                }));
                setErrors((p) => ({ ...p, sources: '' }));
              }}
              disabled={isLocked}
              className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                s2.sources.includes(src)
                  ? 'bg-primary text-primary-foreground border-primary font-bold'
                  : 'border-input text-muted-foreground hover:border-primary'
              }`}
            >
              {src}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Datum för underlag <span className="text-destructive">*</span></Label>
          <Input
            type="date"
            data-testid="s2-evidence-date"
            value={s2.evidence_date}
            onChange={(e) => { setS2((p) => ({ ...p, evidence_date: e.target.value })); setErrors((p) => ({ ...p, evidence_date: '' })); }}
            className={errors.evidence_date ? 'border-destructive' : ''}
            disabled={isLocked}
          />
          {errors.evidence_date && <p role="alert" className="text-xs text-destructive">{errors.evidence_date}</p>}
        </div>

        <div className="space-y-1.5">
          <Label>Utfört av <span className="text-destructive">*</span></Label>
          <select
            data-testid="s2-conducted-by"
            value={s2.conducted_by}
            onChange={(e) => { setS2((p) => ({ ...p, conducted_by: e.target.value })); setErrors((p) => ({ ...p, conducted_by: '' })); }}
            className={selectCls(errors.conducted_by)}
            disabled={isLocked}
          >
            <option value="">Välj...</option>
            {orgUsers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          {errors.conducted_by && <p role="alert" className="text-xs text-destructive">{errors.conducted_by}</p>}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>Bedömningens tillförlitlighet <span className="text-destructive">*</span></Label>
        <select
          data-testid="s2-reliability"
          value={s2.reliability}
          onChange={(e) => { setS2((p) => ({ ...p, reliability: e.target.value })); setErrors((p) => ({ ...p, reliability: '' })); }}
          className={selectCls(errors.reliability)}
          disabled={isLocked}
        >
          <option value="">Välj...</option>
          {RELIABILITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {errors.reliability && <p role="alert" className="text-xs text-destructive">{errors.reliability}</p>}
      </div>

      <div className="space-y-1.5">
        <Label>Underlagsnotering</Label>
        <Textarea data-testid="s2-notes" value={s2.notes} onChange={(e) => setS2((p) => ({ ...p, notes: e.target.value }))} rows={2} disabled={isLocked} />
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4" data-testid="wizard-step-3">
      {errors.areas && (
        <div className="flex items-center gap-2 p-3 bg-destructive/10 rounded-lg text-destructive text-sm">
          <AlertCircle size={14} />
          <span>{errors.areas}</span>
        </div>
      )}
      {areas.map((area, idx) => (
        <Card key={area.area_name} data-testid={`area-row-${idx}`} className="border rounded-xl">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-sm">{area.area_name}</h4>
              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${LEVEL_COLORS[area.current_level] || 'bg-gray-100 text-gray-500'}`}>
                {LEVEL_OPTIONS.find((o) => o.value === area.current_level)?.label}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Nuläge <span className="text-destructive">*</span></Label>
                <select
                  data-testid={`area-level-${idx}`}
                  value={area.current_level}
                  onChange={(e) => {
                    setAreas((prev) => prev.map((a, i) => i === idx ? { ...a, current_level: e.target.value } : a));
                    setErrors((p) => ({ ...p, areas: '' }));
                  }}
                  className={selectCls(false)}
                  disabled={isLocked}
                >
                  {LEVEL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Prioritet</Label>
                <select
                  data-testid={`area-priority-${idx}`}
                  value={area.priority}
                  onChange={(e) => setAreas((prev) => prev.map((a, i) => i === idx ? { ...a, priority: e.target.value } : a))}
                  className={selectCls(false)}
                  disabled={isLocked}
                >
                  {PRIORITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
            {area.current_level !== 'Ej_bedomd' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Styrkor</Label>
                  <Textarea
                    data-testid={`area-strengths-${idx}`}
                    value={area.strengths}
                    onChange={(e) => setAreas((prev) => prev.map((a, i) => i === idx ? { ...a, strengths: e.target.value } : a))}
                    rows={2}
                    disabled={isLocked}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Hinder</Label>
                  <Textarea
                    data-testid={`area-obstacles-${idx}`}
                    value={area.obstacles}
                    onChange={(e) => setAreas((prev) => prev.map((a, i) => i === idx ? { ...a, obstacles: e.target.value } : a))}
                    rows={2}
                    disabled={isLocked}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Behov av stöd</Label>
                  <Textarea
                    data-testid={`area-support-${idx}`}
                    value={area.support_needs}
                    onChange={(e) => setAreas((prev) => prev.map((a, i) => i === idx ? { ...a, support_needs: e.target.value } : a))}
                    rows={2}
                    disabled={isLocked}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Kommentar</Label>
                  <Textarea
                    data-testid={`area-comment-${idx}`}
                    value={area.comment}
                    onChange={(e) => setAreas((prev) => prev.map((a, i) => i === idx ? { ...a, comment: e.target.value } : a))}
                    rows={2}
                    disabled={isLocked}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-4" data-testid="wizard-step-4">
      <div className="flex justify-end">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setAdaptations((p) => [...p, emptyAdaptation()])}
          data-testid="add-adaptation-btn"
          className="rounded-full text-xs"
          disabled={isLocked}
        >
          <Plus size={14} className="mr-1" /> Lägg till anpassning
        </Button>
      </div>
      {adaptations.length === 0 && (
        <p className="text-sm text-muted-foreground italic" data-testid="no-adaptations-msg">
          Inga anpassningar tillagda. Klicka "Lägg till anpassning" för att lägga till.
        </p>
      )}
      {adaptations.map((adp, idx) => (
        <Card key={idx} data-testid={`adaptation-row-${idx}`} className="border rounded-xl">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-muted-foreground">Anpassning {idx + 1}</span>
              {!isLocked && (
                <button
                  type="button"
                  onClick={() => setAdaptations((p) => p.filter((_, i) => i !== idx))}
                  data-testid={`remove-adaptation-${idx}`}
                  className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-destructive"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Anpassning <span className="text-destructive">*</span></Label>
                <select
                  data-testid={`adp-name-${idx}`}
                  value={adp.adaptation_name}
                  onChange={(e) => {
                    const found = ADAPTATION_CATALOG.find((c) => c.name === e.target.value);
                    setAdaptations((p) => p.map((a, i) => i === idx ? { ...a, adaptation_name: e.target.value, main_group: found?.main_group || '' } : a));
                  }}
                  className={selectCls(false)}
                  disabled={isLocked}
                >
                  <option value="">Välj anpassning...</option>
                  {ADAPTATION_CATALOG.map((c) => <option key={c.name} value={c.name}>{c.main_group}: {c.name}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Huvudgrupp (autofylls)</Label>
                <Input value={adp.main_group} readOnly className="bg-muted" data-testid={`adp-group-${idx}`} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Kopplat område</Label>
                <select
                  data-testid={`adp-linked-area-${idx}`}
                  value={adp.linked_area_id}
                  onChange={(e) => setAdaptations((p) => p.map((a, i) => i === idx ? { ...a, linked_area_id: e.target.value } : a))}
                  className={selectCls(false)}
                  disabled={isLocked}
                >
                  <option value="">— Inget kopplat område —</option>
                  {areas.filter((a) => a.current_level !== 'Ej_bedomd').map((a, ai) => (
                    <option key={ai} value={a.area_name}>{a.area_name}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Prioritet <span className="text-destructive">*</span></Label>
                <select
                  data-testid={`adp-priority-${idx}`}
                  value={adp.priority}
                  onChange={(e) => setAdaptations((p) => p.map((a, i) => i === idx ? { ...a, priority: e.target.value } : a))}
                  className={selectCls(false)}
                  disabled={isLocked}
                >
                  {PRIORITY_OPTIONS.filter((o) => o.value).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Ansvarig</Label>
                <select
                  data-testid={`adp-responsible-${idx}`}
                  value={adp.responsible_user_id}
                  onChange={(e) => setAdaptations((p) => p.map((a, i) => i === idx ? { ...a, responsible_user_id: e.target.value } : a))}
                  className={selectCls(false)}
                  disabled={isLocked}
                >
                  <option value="">Ingen vald</option>
                  {orgUsers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Status <span className="text-destructive">*</span></Label>
                <select
                  data-testid={`adp-status-${idx}`}
                  value={adp.status}
                  onChange={(e) => setAdaptations((p) => p.map((a, i) => i === idx ? { ...a, status: e.target.value } : a))}
                  className={selectCls(false)}
                  disabled={isLocked}
                >
                  {ADAPTATION_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Kommentar</Label>
              <Textarea
                data-testid={`adp-comment-${idx}`}
                value={adp.comment}
                onChange={(e) => setAdaptations((p) => p.map((a, i) => i === idx ? { ...a, comment: e.target.value } : a))}
                rows={2}
                disabled={isLocked}
              />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  const renderStep5 = () => (
    <div className="space-y-5" data-testid="wizard-step-5">
      <div className="space-y-1.5">
        <Label>Huvudmål <span className="text-destructive">*</span></Label>
        <Textarea
          data-testid="s5-main-goal"
          value={s5.main_goal}
          onChange={(e) => { setS5((p) => ({ ...p, main_goal: e.target.value })); setErrors((p) => ({ ...p, main_goal: '' })); }}
          rows={3}
          className={errors.main_goal ? 'border-destructive' : ''}
          disabled={isLocked}
        />
        {errors.main_goal && <p role="alert" className="text-xs text-destructive">{errors.main_goal}</p>}
      </div>

      <div className="space-y-2">
        <Label className="flex items-center justify-between">
          Delmål
          {!isLocked && (
            <button type="button" onClick={() => setS5((p) => ({ ...p, sub_goals: [...p.sub_goals, ''] }))} className="text-xs text-primary hover:underline flex items-center gap-1">
              <Plus size={12} /> Lägg till
            </button>
          )}
        </Label>
        {s5.sub_goals.map((sg, idx) => (
          <div key={idx} className="flex gap-2" data-testid={`subgoal-row-${idx}`}>
            <Input
              data-testid={`subgoal-input-${idx}`}
              value={sg}
              onChange={(e) => setS5((p) => ({ ...p, sub_goals: p.sub_goals.map((g, i) => i === idx ? e.target.value : g) }))}
              placeholder={`Delmål ${idx + 1}`}
              disabled={isLocked}
            />
            {!isLocked && (
              <button type="button" onClick={() => setS5((p) => ({ ...p, sub_goals: p.sub_goals.filter((_, i) => i !== idx) }))} data-testid={`remove-subgoal-${idx}`} className="p-2 hover:text-destructive text-muted-foreground">
                <X size={14} />
              </button>
            )}
          </div>
        ))}
      </div>

      <div className="space-y-1.5">
        <Label>Rekommenderad nästa insats <span className="text-destructive">*</span></Label>
        <select
          data-testid="s5-next-intervention"
          value={s5.recommended_next_intervention}
          onChange={(e) => { setS5((p) => ({ ...p, recommended_next_intervention: e.target.value })); setErrors((p) => ({ ...p, recommended_next_intervention: '' })); }}
          className={selectCls(errors.recommended_next_intervention)}
          disabled={isLocked}
        >
          <option value="">Välj insats...</option>
          {getNextInterventionOptions().map((opt) => <option key={opt} value={opt}>{opt}</option>)}
        </select>
        {errors.recommended_next_intervention && <p role="alert" className="text-xs text-destructive">{errors.recommended_next_intervention}</p>}
      </div>

      <div className="space-y-1.5">
        <Label>Motivering <span className="text-destructive">*</span></Label>
        <Textarea
          data-testid="s5-motivation"
          value={s5.motivation}
          onChange={(e) => { setS5((p) => ({ ...p, motivation: e.target.value })); setErrors((p) => ({ ...p, motivation: '' })); }}
          rows={3}
          className={errors.motivation ? 'border-destructive' : ''}
          disabled={isLocked}
        />
        {errors.motivation && <p role="alert" className="text-xs text-destructive">{errors.motivation}</p>}
      </div>

      <div className="space-y-1.5">
        <Label>Startberedskap <span className="text-destructive">*</span></Label>
        <select
          data-testid="s5-readiness"
          value={s5.readiness}
          onChange={(e) => { setS5((p) => ({ ...p, readiness: e.target.value })); setErrors((p) => ({ ...p, readiness: '' })); }}
          className={selectCls(errors.readiness)}
          disabled={isLocked}
        >
          <option value="">Välj...</option>
          {READINESS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {errors.readiness && <p role="alert" className="text-xs text-destructive">{errors.readiness}</p>}
      </div>

      <div className="space-y-1.5">
        <Label>Riskfaktorer</Label>
        <Textarea data-testid="s5-risk-factors" value={s5.risk_factors} onChange={(e) => setS5((p) => ({ ...p, risk_factors: e.target.value }))} rows={2} disabled={isLocked} />
      </div>

      <div className="space-y-1.5">
        <Label>Övrig rekommendation</Label>
        <Textarea data-testid="s5-other" value={s5.other_recommendations} onChange={(e) => setS5((p) => ({ ...p, other_recommendations: e.target.value }))} rows={2} disabled={isLocked} />
      </div>
    </div>
  );

  const renderStep6 = () => (
    <div className="space-y-5" data-testid="wizard-step-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Uppföljningsfrekvens <span className="text-destructive">*</span></Label>
          <select
            data-testid="s6-frequency"
            value={s6.frequency}
            onChange={(e) => {
              const freq = e.target.value;
              setS6((p) => ({ ...p, frequency: freq, next_date: nextDateFromFrequency(freq) }));
              setErrors((p) => ({ ...p, frequency: '' }));
            }}
            className={selectCls(errors.frequency)}
            disabled={isLocked}
          >
            {FOLLOWUP_FREQ_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {errors.frequency && <p role="alert" className="text-xs text-destructive">{errors.frequency}</p>}
        </div>

        <div className="space-y-1.5">
          <Label>Nästa uppföljningsdatum <span className="text-destructive">*</span></Label>
          <Input
            type="date"
            data-testid="s6-next-date"
            value={s6.next_date}
            onChange={(e) => { setS6((p) => ({ ...p, next_date: e.target.value })); setErrors((p) => ({ ...p, next_date: '' })); }}
            className={errors.next_date ? 'border-destructive' : ''}
            disabled={isLocked}
          />
          {errors.next_date && <p role="alert" className="text-xs text-destructive">{errors.next_date}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Ansvarig uppföljare <span className="text-destructive">*</span></Label>
          <select
            data-testid="s6-responsible"
            value={s6.responsible_user_id}
            onChange={(e) => { setS6((p) => ({ ...p, responsible_user_id: e.target.value })); setErrors((p) => ({ ...p, responsible_user_id: '' })); }}
            className={selectCls(errors.responsible_user_id)}
            disabled={isLocked}
          >
            <option value="">Välj...</option>
            {orgUsers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          {errors.responsible_user_id && <p role="alert" className="text-xs text-destructive">{errors.responsible_user_id}</p>}
        </div>

        <div className="space-y-1.5">
          <Label>Uppföljningsform <span className="text-destructive">*</span></Label>
          <select
            data-testid="s6-form"
            value={s6.followup_form}
            onChange={(e) => { setS6((p) => ({ ...p, followup_form: e.target.value })); setErrors((p) => ({ ...p, followup_form: '' })); }}
            className={selectCls(errors.followup_form)}
            disabled={isLocked}
          >
            {FOLLOWUP_FORM_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {errors.followup_form && <p role="alert" className="text-xs text-destructive">{errors.followup_form}</p>}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>Vad ska följas upp <span className="text-destructive">*</span></Label>
        <Textarea
          data-testid="s6-focus"
          value={s6.focus}
          onChange={(e) => { setS6((p) => ({ ...p, focus: e.target.value })); setErrors((p) => ({ ...p, focus: '' })); }}
          rows={3}
          className={errors.focus ? 'border-destructive' : ''}
          disabled={isLocked}
        />
        {errors.focus && <p role="alert" className="text-xs text-destructive">{errors.focus}</p>}
      </div>

      <div className="space-y-1.5">
        <Label>Trigger för tidigare uppföljning</Label>
        <Textarea data-testid="s6-trigger" value={s6.early_trigger} onChange={(e) => setS6((p) => ({ ...p, early_trigger: e.target.value }))} rows={2} disabled={isLocked} />
      </div>

      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          data-testid="s6-reminder"
          checked={s6.reminder_enabled}
          onChange={(e) => setS6((p) => ({ ...p, reminder_enabled: e.target.checked }))}
          className="w-4 h-4 rounded border-input"
          disabled={isLocked}
        />
        <span className="text-sm">Aktivera påminnelse</span>
      </label>
    </div>
  );

  const renderStep7 = () => (
    <div className="space-y-6" data-testid="wizard-step-7">
      {isLocked && (
        <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl text-green-800">
          <ShieldCheck size={20} />
          <div>
            <p className="font-bold text-sm">Kartläggningen är fastställd</p>
            <p className="text-xs mt-0.5">Skrivskyddad — skapa en revidering för att ändra.</p>
          </div>
        </div>
      )}
      {!canEstablish && !isLocked && (
        <div className="flex items-center gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-xl text-yellow-800">
          <AlertCircle size={18} />
          <p className="text-sm">Steg {[1,2,3,5,6].filter((s) => !completeness[s]).join(', ')} är ännu ofullständiga.</p>
        </div>
      )}

      {/* Summary sections */}
      <Card className="border rounded-xl">
        <CardContent className="p-4 space-y-2">
          <h4 className="font-bold text-sm">Steg 1 — Grunduppgifter</h4>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
            <span className="text-muted-foreground">Deltagare</span><span>{participantName(s1.participant_id)}</span>
            <span className="text-muted-foreground">Typ</span><span>{ASSESSMENT_TYPE_LABELS[s1.assessment_type]}</span>
            <span className="text-muted-foreground">Startdatum</span><span>{s1.start_date}</span>
            <span className="text-muted-foreground">Ansvarig</span><span>{userName(s1.responsible_user_id)}</span>
            <span className="text-muted-foreground">Syfte</span><span className="col-span-1">{s1.purpose}</span>
          </div>
        </CardContent>
      </Card>

      <Card className="border rounded-xl">
        <CardContent className="p-4 space-y-2">
          <h4 className="font-bold text-sm">Steg 2 — Bedömningsunderlag</h4>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
            <span className="text-muted-foreground">Källor</span><span>{s2.sources.join(', ') || '—'}</span>
            <span className="text-muted-foreground">Utfört av</span><span>{userName(s2.conducted_by)}</span>
            <span className="text-muted-foreground">Tillförlitlighet</span><span>{RELIABILITY_OPTIONS.find((o) => o.value === s2.reliability)?.label || '—'}</span>
          </div>
        </CardContent>
      </Card>

      <Card className="border rounded-xl">
        <CardContent className="p-4 space-y-2">
          <h4 className="font-bold text-sm">Steg 3 — Bedömningsområden</h4>
          <div className="space-y-1">
            {areas.filter((a) => a.current_level !== 'Ej_bedomd').map((a, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span>{a.area_name}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${LEVEL_COLORS[a.current_level]}`}>
                  {LEVEL_OPTIONS.find((o) => o.value === a.current_level)?.label}
                </span>
              </div>
            ))}
            {areas.every((a) => a.current_level === 'Ej_bedomd') && <p className="text-sm text-muted-foreground italic">Inga bedömda områden ännu.</p>}
          </div>
        </CardContent>
      </Card>

      {s5.main_goal && (
        <Card className="border rounded-xl">
          <CardContent className="p-4 space-y-2">
            <h4 className="font-bold text-sm">Steg 5 — Mål och rekommendation</h4>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <span className="text-muted-foreground">Huvudmål</span><span>{s5.main_goal}</span>
              <span className="text-muted-foreground">Rekommenderad insats</span><span>{s5.recommended_next_intervention}</span>
              <span className="text-muted-foreground">Startberedskap</span><span>{READINESS_OPTIONS.find((o) => o.value === s5.readiness)?.label || '—'}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {s6.focus && (
        <Card className="border rounded-xl">
          <CardContent className="p-4 space-y-2">
            <h4 className="font-bold text-sm">Steg 6 — Uppföljningsplan</h4>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <span className="text-muted-foreground">Frekvens</span><span>{FOLLOWUP_FREQ_OPTIONS.find((o) => o.value === s6.frequency)?.label}</span>
              <span className="text-muted-foreground">Nästa datum</span><span>{s6.next_date}</span>
              <span className="text-muted-foreground">Form</span><span>{FOLLOWUP_FORM_OPTIONS.find((o) => o.value === s6.followup_form)?.label}</span>
              <span className="text-muted-foreground">Fokus</span><span>{s6.focus}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action buttons */}
      {!isLocked && (
        <div className="flex flex-wrap gap-3 pt-4 border-t" data-testid="step7-actions">
          <Button type="button" variant="outline" onClick={handleSaveDraft} disabled={!assessmentId || saving} data-testid="save-draft-btn" className="rounded-full">
            {saving ? 'Sparar...' : 'Spara som utkast'}
          </Button>
          <Button type="button" variant="outline" onClick={handleMarkOngoing} disabled={!assessmentId || saving} data-testid="mark-ongoing-btn" className="rounded-full">
            Markera som pågående
          </Button>
          <Button
            type="button"
            onClick={handleEstablish}
            disabled={!assessmentId || !canEstablish || establishing}
            data-testid="establish-btn"
            className="rounded-full font-bold px-8"
            style={{ background: canEstablish ? '#F9CF3F' : undefined, color: canEstablish ? '#333333' : undefined }}
          >
            {establishing ? 'Fastställer...' : 'Fastställ kartläggning'}
          </Button>
        </div>
      )}
    </div>
  );

  const STEP_RENDERERS = { 1: renderStep1, 2: renderStep2, 3: renderStep3, 4: renderStep4, 5: renderStep5, 6: renderStep6, 7: renderStep7 };

  // ── Main render ────────────────────────────────────────────────────────────────
  return (
    <>
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto p-4" data-testid="assessment-wizard">
      <div className="w-full max-w-3xl bg-background rounded-2xl shadow-2xl my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-2xl font-heading font-black" data-testid="wizard-title">
              {assessmentId ? 'Redigera kartläggning' : 'Ny kartläggning'}
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              {STEP_LABELS[step - 1]} — Steg {step} av {STEP_LABELS.length}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="wizard-close-btn"
            className="p-2 rounded-full hover:bg-muted text-muted-foreground"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          <StepIndicator current={step} completeness={completeness} />
          {STEP_RENDERERS[step]?.()}
        </div>

        {/* Footer navigation */}
        <div className="flex items-center justify-between p-6 border-t bg-muted/30 rounded-b-2xl">
          <Button
            type="button"
            variant="ghost"
            onClick={() => step === 1 ? onClose?.() : goToStep(step - 1)}
            data-testid="prev-btn"
            className="rounded-full"
          >
            {step === 1 ? (
              'Avbryt'
            ) : (
              <><ChevronLeft size={16} className="mr-1" /> Föregående</>
            )}
          </Button>
          {step < 7 && (
            <Button
              type="button"
              onClick={() => goToStep(step + 1)}
              disabled={saving}
              data-testid="next-btn"
              className="rounded-full font-bold px-8"
              style={{ background: '#F9CF3F', color: '#333333' }}
            >
              {saving ? 'Sparar...' : <>{step === 6 ? 'Sammanfattning' : 'Nästa'} <ChevronRight size={16} className="ml-1" /></>}
            </Button>
          )}
        </div>
      </div>
    </div>

      <Dialog open={showGoalDialog} onOpenChange={(open) => { if (!open) handleGoalDialogClose(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Kartläggningen är fastställd</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Vill du skapa mål baserat på rekommendationerna från kartläggningen?
          </p>
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="ghost" className="rounded-full" onClick={handleGoalDialogClose}>
              Inte nu
            </Button>
            <Button
              type="button"
              className="rounded-full font-bold"
              style={{ background: '#F9CF3F', color: '#333333' }}
              onClick={handleNavigateToGoals}
            >
              Skapa mål
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AssessmentWizard;
